import type { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useGetMyItemsDashboard } from '@/hooks/queries';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';

import { useGetTypes } from './config';
import useGetClickthroughEnabled from './hooks/useGetClickthroughEnabled';
import { useGetQueryVariables } from './hooks/useGetQueryVariables';
import { useGetRibbonItemVisibilities } from './hooks/useGetRibbonItemVisibilities';
import { useGetStatusFilterOptions } from './hooks/useGetStatusFilterOptions';
import { useGetTypeFilterOptions } from './hooks/useGetTypeFilterOptions';
import { useFlattenRibbonData } from './useFlattenRibbonData';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';
import { useRibbonNavigation } from './useRibbonNavigation';

const DEFAULT_FILTER_QUERY: PropertyFilterQuery = {
  tokens: [],
  operation: 'and',
};

// Filtering properties for the ribbon's "Edit ribbon" modal.
// propertyLabel strings mirror the TableFields headers in config.tsx.
const FILTERING_PROPERTIES = [
  {
    key: 'StatusLabelled',
    propertyLabel: 'Object Status',
    groupValuesLabel: 'Object Status values',
    operators: ['=', '!=', ':', '!:'] as const,
  },
  {
    key: 'Type',
    propertyLabel: 'Type',
    groupValuesLabel: 'Type values',
    operators: ['=', '!=', ':', '!:'] as const,
  },
];

const Ribbon: FC = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.myItemsDashboard.ribbonTitles',
  });
  const { t: st } = useTranslation(['common'], { keyPrefix: 'dashboard' });
  const itemVisibilities = useGetRibbonItemVisibilities();
  const types = useGetTypes();

  const noClickthroughMessageContent = useMemo(
    () => st('my_items_filters_alert'),
    [st]
  );

  const clickThroughEnabled = useGetClickthroughEnabled();

  // Apollo (flag-off) variables still come from the legacy where-clause builder;
  // the tRPC (flag-on) input is derived inside the hook from the same store.
  const queryVars = useGetQueryVariables();

  const { data, loading } = useGetMyItemsDashboard(queryVars);

  // Tracks which tile is visually selected (highlighted)
  const [propertyFilterQuery, setPropertyFilterQuery] =
    useState<PropertyFilterQuery>(DEFAULT_FILTER_QUERY);

  const ribbonItems = useFlattenRibbonData(data);
  const statusOptions = useGetStatusFilterOptions();
  const typeOptions = useGetTypeFilterOptions();

  const filteringOptions = useMemo(
    // Both Status and Type options are fixed vocabularies (every possible
    // entity's statuses and every supported Type label) so admins can
    // select, remove, and re-add tokens freely even when no matching items
    // are currently loaded — see useGetStatus/TypeFilterOptions.
    () => [...statusOptions, ...typeOptions],
    [statusOptions, typeOptions]
  );

  const { ribbonProps } = useRibbonAndExport(useGetDefaultRibbonFilters);

  // Navigates to the entity register page matching the Type token in the query.
  // Also updates local state so the clicked tile stays highlighted.
  const navigateOnTileClick = useRibbonNavigation(clickThroughEnabled, data);
  const handleTileClick = (query: PropertyFilterQuery) => {
    if (!clickThroughEnabled) {
      return;
    }
    setPropertyFilterQuery(query);
    navigateOnTileClick(query);
  };

  const leftPanelDefaultFilters = useMemo((): FilterModal[] => {
    const filters: FilterModal[] = [];
    if (itemVisibilities.approvals) {
      filters.push({
        id: 'approval',
        title: t('approvals'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            { operator: '=', propertyKey: 'Type', value: types.request },
          ],
          operation: 'and',
        },
      });
    }
    if (itemVisibilities.attestations) {
      filters.push({
        id: 'attestation',
        title: t('attestations'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            { operator: '=', propertyKey: 'Type', value: types.attestation },
          ],
          operation: 'and',
        },
      });
    }

    return filters;
  }, [itemVisibilities.approvals, itemVisibilities.attestations, t, types]);

  return (
    <div className={'flex gap-3'}>
      {(itemVisibilities.approvals || itemVisibilities.attestations) && (
        <div className={'shrink-0'}>
          <CustomisableRibbon
            items={ribbonItems}
            loading={loading}
            propertyFilterQuery={propertyFilterQuery}
            onFilterQueryChanged={handleTileClick}
            filteringProperties={FILTERING_PROPERTIES}
            filteringOptions={filteringOptions}
            defaultFilters={leftPanelDefaultFilters}
            clickThroughEnabled={clickThroughEnabled}
            noClickthroughMessageContent={noClickthroughMessageContent}
            showEditButton={false}
          />
        </div>
      )}
      <div data-testid={'my-items-ribbon'} className={'min-w-0 flex-1'}>
        <CustomisableRibbon
          items={ribbonItems}
          loading={loading}
          propertyFilterQuery={propertyFilterQuery}
          onFilterQueryChanged={handleTileClick}
          filteringProperties={FILTERING_PROPERTIES}
          filteringOptions={filteringOptions}
          parentType={Parent_Type_Enum.MyItems}
          clickThroughEnabled={clickThroughEnabled}
          noClickthroughMessageContent={noClickthroughMessageContent}
          {...ribbonProps}
          showEditButton={false}
        />
      </div>
    </div>
  );
};

export default Ribbon;
